/** @jsxImportSource @emotion/react */

import { useCallback } from "react";
import { useIntl } from "react-intl";
import { useLocation, useHistory } from "react-router-dom";

import { css } from "@emotion/react";
import { Box, Typography } from "@material-ui/core";

import { useAccount } from "../context/account";
import { useModal } from "../context/modal";
import theme from "../styles/theme";
import { Tier } from "../types";

const dialogContent = css`
  width: 400px;
  height: 107px;
  overflow-y: auto;
  padding: ${theme.spacing(0, 2.5)};
  margin-bottom: ${theme.spacing(1.5)}px;

  .MuiTypography-root {
    line-height: normal;
  }
`;

interface Args {
  content?: string;
  cancelBtnText?: string;
  cancelBtnClick?: () => void;
}

interface Return {
  popupUpgradeModal: (args?: Args) => void;
  requestInfo: () => Promise<{
    shouldUpgrade: boolean;
    isEnterprise: boolean;
    isFreetrial: boolean;
  }>;
}

const useUpgradePlan = (): Return => {
  const intl = useIntl();
  const { pathname } = useLocation();
  const { push } = useHistory();
  const [showModal, hideModal] = useModal();
  const { tier, availableMins, getAccount } = useAccount();

  const popupUpgradeModal = useCallback(
    (args = {}) => {
      const { content, cancelBtnText, cancelBtnClick } = args;
      showModal({
        title: intl.formatMessage({
          id: "popout-title.upgrade",
        }),
        content: (
          <Box css={dialogContent}>
            <Typography variant="body2" gutterBottom>
              {content ||
                intl.formatMessage({
                  id: "popout-context.upgrade",
                })}
            </Typography>
          </Box>
        ),
        actions: [
          {
            color: "white",
            label: cancelBtnText || intl.formatMessage({ id: "btn.later" }),
            onClick: () => {
              hideModal();
              cancelBtnClick?.();
            },
          },
          {
            primary: true,
            label: intl.formatMessage({ id: "btn.upgrade" }),
            onClick: () => {
              hideModal();
              push("/pricing", { from: pathname });
            },
          },
        ],
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hideModal, showModal]
  );

  const getUpgradeInfo = (currentTier: Tier, currentAvailableMins: number | undefined) => ({
    shouldUpgrade:
      currentTier === Tier.OFFICIAL && typeof currentAvailableMins === "number" ? currentAvailableMins <= 0 : false,
    isEnterprise: currentTier === Tier.ENTERPRISE,
    isFreetrial: currentTier === Tier.FREE_TRIAL,
  });

  const requestInfo = useCallback(async () => {
    if (tier === Tier.ENTERPRISE) {
      return getUpgradeInfo(tier, availableMins);
    }

    // eslint-disable-next-line no-useless-catch
    try {
      const data = await getAccount();
      return getUpgradeInfo(data?.org_tier, data?.available_mins);
    } catch (error) {
      throw error;
    }
  }, [tier, availableMins, getAccount]);

  return {
    requestInfo,
    popupUpgradeModal,
  };
};

export default useUpgradePlan;
